import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {PosSizeService} from './pos-size.service';

export interface PosUiScalePreset {
  id: 'compact' | 'default' | 'large' | 'xlarge';
  label: string;
  scale: number;
}

/**
 * Merchant-adjustable POS UI scale (ENG-5076).
 *
 * Exposes four QA-approved presets and applies the selected one by setting
 * the --pos-scale CSS custom property on <html>. Typography and spacing
 * design tokens in src/styles/_design-tokens.scss multiply by this value,
 * so every token consumer scales without per-component changes.
 *
 * ENG-5148: The selection is store-wide — the source of truth is the
 * Store.pos_ui_scale field on the backend, synced here whenever store data
 * loads (syncFromStore). localStorage only caches the last-known value so
 * the scale applies instantly on boot, before the store API responds.
 *
 * ENG-5842: the presets are now the LEGACY input of PosSizeService (the Text size and
 * Button size sliders), which writes every POS size variable. A store that never moved
 * a slider still gets exactly this preset.
 */
@Injectable({providedIn: 'root'})
export class PosUiScaleService {

  static readonly STORAGE_KEY = 'posUiScale';

  /**
   * Scale a base pixel value for a POS dialog width (e.g. '680px' → '884px' at 1.3×). Dialogs hold
   * text as well as buttons, so they follow the larger of the Button and Text size (ENG-5842).
   */
  static scaledPx(base: number): string {
    const root = getComputedStyle(document.documentElement);
    const b = parseFloat(root.getPropertyValue('--pos-scale') || '1') || 1;
    const t = parseFloat(root.getPropertyValue('--pos-text-scale') || '1') || 1;
    return `${Math.round(base * Math.max(b, t))}px`;
  }

  constructor(private size: PosSizeService) {}

  readonly presets: PosUiScalePreset[] = [
    {id: 'compact', label: 'Compact', scale: 0.9},
    {id: 'default', label: 'Default', scale: 1},
    {id: 'large', label: 'Large', scale: 1.15},
    {id: 'xlarge', label: 'Extra Large', scale: 1.3},
  ];

  private currentSubject = new BehaviorSubject<PosUiScalePreset>(this.presets[1]);

  get current(): PosUiScalePreset {
    return this.currentSubject.value;
  }

  get current$(): Observable<PosUiScalePreset> {
    return this.currentSubject.asObservable();
  }

  /** Restore the persisted preset and apply it. Called once at app start. */
  init(): void {
    const savedId = localStorage.getItem(PosUiScaleService.STORAGE_KEY);
    const preset = this.presets.find(p => p.id === savedId);
    if (preset) {
      this.currentSubject.next(preset);
    }
    this.apply(this.current);
  }

  setPreset(id: PosUiScalePreset['id']): void {
    const preset = this.presets.find(p => p.id === id);
    if (!preset || preset.id === this.current.id) {
      return;
    }
    localStorage.setItem(PosUiScaleService.STORAGE_KEY, preset.id);
    this.currentSubject.next(preset);
    this.apply(preset);
  }

  /**
   * ENG-5148: Apply the store-wide scale coming from backend store data.
   * Called whenever the current store loads/refreshes. An unset or unknown
   * value is a no-op so stores that never chose a size keep the local value.
   */
  syncFromStore(scaleId: string | null | undefined): void {
    if (!scaleId || scaleId === this.current.id) {
      return;
    }
    const preset = this.presets.find(p => p.id === scaleId);
    if (!preset) {
      return;
    }
    localStorage.setItem(PosUiScaleService.STORAGE_KEY, preset.id);
    this.currentSubject.next(preset);
    this.apply(preset);
  }

  /**
   * Multiplier applied on top of the merchant's size while the Orders module is showing its
   * phone layout: the phone shell was drawn with the desktop type scale and the content did not
   * fit, so this ONE number shrinks type and spacing together everywhere on the phone. 0.9 is the
   * same step as the "Compact" preset. Applied by PosSizeService to both sliders.
   */
  setPhoneFactor(factor: number): void {
    this.size.setPhoneFactor(factor);
  }

  private apply(preset: PosUiScalePreset): void {
    this.size.setLegacy({scale: preset.scale});
  }
}
